import { useState, useRef, useEffect } from 'react';
import { Box, TextField, Portal, ClickAwayListener, colors, Paper } from '@mui/material';
import { useRichTextEditor } from './RichTextEditorProvider';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { IconButton } from '@sweep-io/sweep-design';
import { LinkBreak } from '@sweep-io/sweep-design/dist/icons';

interface LinkPopupProps {
  position: { top: number; left: number } | null;
  onClose: () => void;
  initialUrl?: string;
}

export const LinkPopup = ({ position, onClose, initialUrl = '' }: LinkPopupProps) => {
  const { editor } = useRichTextEditor();
  const [url, setUrl] = useState(initialUrl);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl]);

  useEffect(() => {
    // Focus the input when the popup opens
    if (position && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [position]);

  const handleInsertLink = () => {
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url,
        target: '_blank',
        rel: 'noopener noreferrer',
      });
    }
    onClose();
  };

  const handleRemoveLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsertLink();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!position) return null;

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleInsertLink}>
        <Paper
          elevation={1}
          sx={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: 'translate(-50%, -100%)',
            marginTop: '-12px',
            zIndex: 10000,
            borderRadius: '4px',
          }}
        >
          {/* Arrow pointing down */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${colors.grey[900]}`,
              zIndex: 1,
            }}
          />

          {/* Main popup box */}
          <Paper
            elevation={1}
            sx={{
              backgroundColor: colors.grey[900],
              borderRadius: '8px',
              minWidth: '320px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              inputRef={inputRef}
              size="small"
              placeholder="Type or paste URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.grey[900],
                  color: 'white',
                  borderRadius: '4px',
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: colors.grey[500],
                  },
                },
              }}
            />
            {initialUrl && (
              <IconButton variant="dark" size="small" onClick={handleRemoveLink}>
                <LinkBreak />
              </IconButton>
            )}
          </Paper>
        </Paper>
      </ClickAwayListener>
    </Portal>
  );
};
